import { useState } from "react";
import { FiAlertTriangle, FiCheck, FiCopy, FiInbox } from "react-icons/fi";
import moment from "moment";
import "../Dashboard/dashboard.css";
import "./memberProfile.css";

export const formatDay = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD MMM YYYY") : "—";
};

export const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const PILL_TONE = {
  pending: "warn",
  approved: "ok",
  disbursed: "",
  paid: "ok",
  declined: "bad",
  overdue: "bad",
  due: "warn",
};

export function StatusPill({ status, children }) {
  const key = String(status || "").toLowerCase();
  return <span className={`mp-pill ${PILL_TONE[key] ?? "mute"}`}>{children || titleCase(key) || "Unknown"}</span>;
}

/** Small copy-to-clipboard button; falls back silently where the clipboard is blocked. */
export function CopyButton({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  if (!text || text === "N/A" || text === "---") return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(text));
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch (e) {
      /* clipboard unavailable: nothing to do */
    }
  };
  return (
    <button type="button" className="mp-copy" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      {done ? <FiCheck size={14} aria-hidden="true" /> : <FiCopy size={14} aria-hidden="true" />}
    </button>
  );
}

/**
 * One place for "nothing here" and "could not load". `tone="warn"` is for the second, so a
 * failed or missing load can never be mistaken for a member with no records.
 */
export function Notice({ tone = "empty", title, children, actionLabel, onAction }) {
  const Icon = tone === "warn" ? FiAlertTriangle : FiInbox;
  return (
    <div className={`mp-notice ${tone === "warn" ? "warn" : ""}`} role={tone === "warn" ? "alert" : undefined}>
      <span className="mp-notice-icon">
        <Icon size={20} aria-hidden="true" />
      </span>
      <h3 className="mp-notice-title">{title}</h3>
      {children ? <p>{children}</p> : null}
      {actionLabel && onAction ? (
        <button type="button" className="mp-btn primary sm" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

/** Heading, a one-line summary, and optional summary cards above a tab's table. */
export function PanelHead({ title, summary, stats }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <h3 className="mp-h" style={{ marginBottom: 2 }}>{title}</h3>
      {summary ? <p className="mp-mute">{summary}</p> : null}
      {stats && stats.length ? (
        <div className="mp-stats" style={{ marginTop: 12, marginBottom: 0 }}>
          {stats.map((stat) => (
            <div key={stat.label} className={`mp-statcard ${stat.tone ? `tone-${stat.tone}` : ""} ${stat.quiet ? "quiet" : ""}`}>
              <p className="l">{stat.label}</p>
              <p className="n">{stat.value}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
